/**
 * SaveToMediaLibraryModal - Modal for saving generated images to Media Library.
 */
import { useCallback, useEffect, useState } from 'react';
import { useCreativeStudioStore, useSelectedImage } from '@/store/creativeStudioStore';
import { useSaveToMediaLibrary } from '@/hooks/queries/useCreativeStudioQueries';
import { useMediaFolders, useMediaTags } from '@/hooks/queries/useMediaQueries';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

export function SaveToMediaLibraryModal() {
  const {
    showSaveModal,
    setShowSaveModal,
    currentJobId,
    refinementSessionId,
    selectedImageIndex,
  } = useCreativeStudioStore();

  const selectedImage = useSelectedImage();
  const { data: folders } = useMediaFolders();
  const { data: tags } = useMediaTags();
  const saveMutation = useSaveToMediaLibrary();

  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [altText, setAltText] = useState('');
  const [description, setDescription] = useState('Generated by Creative Studio');
  const [filename, setFilename] = useState('');
  const [saveAsReference, setSaveAsReference] = useState(false);

  const handleSave = useCallback(async () => {
    try {
      // Only send session_id OR generation_job_id, not both.
      // Refinement session takes precedence (includes refinement results).
      // For sessions: omit image_index to save the current (latest) image.
      // For generation jobs: pass selectedImageIndex to save specific generated image.
      const result = await saveMutation.mutateAsync({
        session_id: refinementSessionId || undefined,
        generation_job_id: refinementSessionId ? undefined : (currentJobId || undefined),
        image_index: refinementSessionId ? undefined : selectedImageIndex,
        folder_id: selectedFolderId || undefined,
        alt_text: altText || undefined,
        description: description || undefined,
        tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
        filename: filename || undefined,
        category: saveAsReference ? 'reference' : 'approved',
      });

      if (result.success) {
        toast.success('Image saved to Media Library');
        setShowSaveModal(false);
        // Reset form
        setSelectedFolderId('');
        setSelectedTagIds([]);
        setAltText('');
        setFilename('');
        setSaveAsReference(false);
      } else {
        toast.error(result.message || 'Failed to save image');
      }
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save image to Media Library');
    }
  }, [
    saveMutation,
    refinementSessionId,
    currentJobId,
    selectedImageIndex,
    selectedFolderId,
    selectedTagIds,
    altText,
    description,
    filename,
    saveAsReference,
    setShowSaveModal,
  ]);

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const isSaving = saveMutation.isPending;

  // Body scroll lock + Escape — mirrors ConfirmDialog so the dialog behaves
  // like the rest of the codebase's modals.
  useEffect(() => {
    if (!showSaveModal) return;
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) {
        setShowSaveModal(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showSaveModal, isSaving, setShowSaveModal]);

  if (!showSaveModal) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-to-media-library-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        aria-hidden="true"
        onClick={() => {
          if (!isSaving) setShowSaveModal(false);
        }}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2
              id="save-to-media-library-title"
              className="text-xl font-semibold text-gray-900"
            >
              Save to Media Library
            </h2>
            <button
              onClick={() => setShowSaveModal(false)}
              disabled={isSaving}
              className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
              aria-label="Close"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Preview */}
          {selectedImage && (
            <div className="mb-6">
              <div className="relative bg-gray-100 rounded-lg overflow-hidden h-40 flex items-center justify-center">
                {selectedImage.signed_url ? (
                  <img
                    src={selectedImage.signed_url}
                    alt="Preview"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center text-gray-400 text-xs px-4 text-center">
                    <span className="font-medium">No preview available</span>
                    <span>Image will still be saved.</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1 text-center">
                {selectedImage.width} x {selectedImage.height}px
              </p>
            </div>
          )}

          {/* Form */}
          <div className="space-y-4">
            {/* Folder Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Save to Folder
              </label>
              <select
                value={selectedFolderId}
                onChange={(e) => setSelectedFolderId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm
                           focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Root (No Folder)</option>
                {folders?.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.path || folder.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Tags */}
            {tags && tags.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => toggleTag(tag.id)}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors
                        ${
                          selectedTagIds.includes(tag.id)
                            ? 'bg-primary-100 border-primary-500 text-primary-700'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                        }`}
                    >
                      {selectedTagIds.includes(tag.id) && (
                        <CheckIcon className="h-3 w-3 inline mr-1" />
                      )}
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Filename */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filename (Optional)
              </label>
              <Input
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder="custom-filename.png"
              />
            </div>

            {/* Alt Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alt Text (Optional)
              </label>
              <Input
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                placeholder="Describe the image for accessibility"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm
                           focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                           resize-none"
              />
            </div>

            {/* Save as Reference */}
            <div>
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveAsReference}
                  onChange={(e) => setSaveAsReference(e.target.checked)}
                  className="mt-0.5 text-primary-600 focus:ring-primary-500 rounded"
                />
                <span>
                  <span className="font-medium">Save as Reference</span>
                  <span className="block text-xs text-gray-500">
                    Studio input only — hidden from the image selector. Promote later
                    from the library to use it in ads.
                  </span>
                </span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <Button
              variant="secondary"
              size="md"
              onClick={() => setShowSaveModal(false)}
              mobileTouch
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleSave}
              loading={saveMutation.isPending}
              mobileTouch
              className="flex-1"
            >
              Save to Library
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
